import React from 'react';
import { Typography, Row, Col, Button, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ArticleCard from './ArticleCard';
import { pulseArticlesSorted } from '../content/pulse';
import './PulsePage.css';

const { Title, Text, Paragraph } = Typography;

const PulsePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="pulse-page">
      <nav className="pulse-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="pulse-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={18}>
            <header className="pulse-header">
              <Title level={1} className="pulse-title">
                Pulse
              </Title>
              <Text className="pulse-subtitle">
                Strategic Perspective on Life Sciences &amp; Health Care
              </Text>
            </header>

            <section className="pulse-intro" aria-label="About Pulse">
              <Paragraph className="pulse-intro-paragraph">
                Pulse is SNS Intelligence&apos;s strategic briefing for life sciences and health care
                decision-makers. Cutting through the noise of a rapidly evolving industry, Pulse delivers timely,
                analyst-driven perspective on the trends, market shifts, pipeline developments, and competitive
                dynamics shaping pharma, biotech, and the broader health care ecosystem.
              </Paragraph>
              <Paragraph className="pulse-intro-paragraph">
                Designed for executives, strategists, and researchers who need more than data, Pulse offers
                context, interpretation, and forward-looking insight to inform smarter decisions across
                development, commercial, and investment horizons.
              </Paragraph>
            </section>

            <section aria-label="Latest Pulse editions">
              <h2 className="pulse-section-heading">Latest editions</h2>
              <div className="article-card-grid">
                {pulseArticlesSorted.map((article) => (
                  <ArticleCard
                    key={article.slug}
                    href={`/pulse/${article.slug}`}
                    date={article.displayDate}
                    title={article.title}
                    excerpt={article.excerpt}
                  />
                ))}
              </div>
            </section>
          </Col>
        </Row>
      </div>

      <footer className="pulse-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PulsePage;
